import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { sendOrderEmail } from "@/utils/email";
import { ShoppingBag, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { CartItemsList } from "@/components/cart/CartItemsList";
import { CartSummary } from "@/components/cart/CartSummary";

const Cart = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { items, updateQuantity, removeItem, clearCart } = useCart();
  const { user } = useAuth();

  console.log("Rendering Cart with items from context:", items);

  const handleQuantityChange = (itemId: string, change: number) => {
    console.log(`Updating quantity for item ${itemId} by ${change}`);
    updateQuantity(itemId, change);
  };

  const handleRemoveItem = (itemId: string) => {
    console.log(`Removing item ${itemId} from cart`);
    removeItem(itemId);
    toast({
      title: "Article retiré",
      description: "L'article a été retiré de votre panier",
    });
  };

  const initiateOrder = async () => {
    console.log("Starting order initiation process");
    if (!user) {
      console.log("User not authenticated, redirecting to login");
      toast({
        title: "Connexion requise",
        description: "Veuillez vous connecter ou créer un compte pour passer votre commande",
      });
      navigate("/account");
      return;
    }

    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (!profile.first_name || !profile.last_name || !profile.phone || 
          !profile.street_address || !profile.city || !profile.province || 
          !profile.postal_code) {
        console.error("Incomplete profile detected");
        toast({
          title: "Profil incomplet",
          description: "Veuillez compléter votre profil avant de passer commande",
          variant: "destructive",
        });
        navigate("/account");
        return;
      }

      console.log("Profile validation passed, showing confirmation dialog");
      setShowConfirmation(true);
    } catch (error) {
      console.error("Error during order initiation:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la préparation de la commande.",
        variant: "destructive",
      });
    }
  };

  const handleSubmitOrder = async () => {
    console.log("Starting order submission process");
    setIsSubmitting(true);
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const tps = subtotal * 0.05;
      const tvq = subtotal * 0.09975;
      const total = subtotal + tps + tvq;

      console.log("Creating order with calculated totals:", { subtotal, tps, tvq, total });

      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: user.id,
          subtotal,
          tps,
          tvq,
          total,
          shipping_address: `${profile.street_address}, ${profile.city}, ${profile.province} ${profile.postal_code}`,
          customer_email: user.email,
          customer_name: `${profile.first_name} ${profile.last_name}`,
          customer_phone: profile.phone,
        })
        .select('*, order_number')
        .single();

      if (orderError) {
        console.error("Error creating order:", orderError);
        throw orderError;
      }

      console.log("Order created successfully:", order);

      const orderItems = items.map(item => ({
        order_id: order.id,
        product_id: item.id,
        product_name: item.name,
        variant_size: item.variant.size || 'unique',
        variant_color: item.variant.colorName || 'standard',
        quantity: item.quantity,
        unit_price: item.price,
        total_price: item.price * item.quantity,
      }));

      console.log("Creating order items:", orderItems);

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) {
        console.error("Error creating order items:", itemsError);
        throw itemsError;
      }

      console.log("Order items created successfully");

      await sendOrderEmail({
        customerEmail: user.email!,
        customerProfile: {
          first_name: profile.first_name,
          last_name: profile.last_name,
          phone: profile.phone,
          address: `${profile.street_address}, ${profile.city}, ${profile.province} ${profile.postal_code}`,
          company: profile.company,
        },
        products: items,
        orderDetails: {
          subtotal,
          tps,
          tvq,
          total,
          orderId: order.id,
          orderNumber: order.order_number
        },
      });
      
      console.log("Order email sent successfully");
      
      clearCart();
      setShowConfirmation(false);
      navigate("/order-confirmation");
    } catch (error) {
      console.error("Order submission failed:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi de la commande.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50/50 pb-6 sm:pb-10">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="mb-4 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2 mb-3 sm:mb-0">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/")}
              className="mb-2 text-zinc-700 hover:text-[#b82626] hover:bg-white pl-0 sm:pl-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Continuer vos achats
            </Button>
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-zinc-900">Votre Panier</h1>
          </div>
          <div className="text-sm font-medium text-zinc-500">
            {items.length} article{items.length !== 1 ? 's' : ''}
          </div>
        </div>
        
        {items.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-zinc-100 p-6 sm:p-16 text-center">
            <div className="flex flex-col items-center max-w-sm mx-auto">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-zinc-100/80 flex items-center justify-center mb-4 sm:mb-6">
                <ShoppingBag className="w-6 h-6 sm:w-8 sm:h-8 text-zinc-400" />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-zinc-900 mb-2">Votre panier est vide</h2>
              <p className="text-zinc-500 mb-4 sm:mb-6">
                Découvrez notre collection et trouvez les articles qui vous correspondent.
              </p>
              <Button 
                onClick={() => navigate("/")} 
                variant="outline"
                className="hover:bg-zinc-50"
              >
                Découvrir nos produits
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-zinc-100 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-zinc-100">
                  <h2 className="font-medium text-lg text-zinc-900">
                    Détails de la commande
                  </h2>
                </div>
                <CartItemsList
                  items={items}
                  onQuantityChange={handleQuantityChange}
                  onRemoveItem={handleRemoveItem}
                />
              </div>
            </div>
            
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-zinc-100 sticky top-8">
                <CartSummary
                  items={items}
                  isSubmitting={isSubmitting}
                  showConfirmation={showConfirmation}
                  user={user}
                  onInitiateOrder={initiateOrder}
                  onConfirmOrder={handleSubmitOrder}
                  onCancelOrder={() => setShowConfirmation(false)}
                  onLogin={() => navigate("/account")}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
